import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import SHLoader from 'common/loader/SHLoader';
import {SEARCH_TYPE} from './search-prop-types';
import WithSearch from './WithSearch';
import WithParkingNearMe from './WithParkingNearMe';
import SearchLayoutDesktop from './layout/SearchLayoutDesktop';
import SpotList from './spot-list/SpotList';
import SpotListSorter from './spot-list/SpotListSorter';
import SpotListSearchControls from './spot-list/SpotListSearchControls';
import SearchMap from './map/SearchMap';
import SpotListSearchControlsCondensed from './spot-list/SpotListSearchControlsCondensed';
import SpotListItemTransient from './spot-list/SpotListItemTransient';

const DestinationInfoPanel = loadable(() =>
    import('../destination/DestinationInfoPanel')
);
const SpotDetails = loadable(() => import('spot-details/SpotDetails'));

class SearchDesktopTransient extends Component {
    static propTypes = {
        history: PropTypes.shape({
            location: PropTypes.object,
        }),
        sortBy: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        canonicalUrl: PropTypes.string,
        city: PropTypes.object,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpotId: PropTypes.number,
        searchType: SEARCH_TYPE.isRequired,
        searchRequestId: PropTypes.string,
        isInfoPanelVisible: PropTypes.bool,
        findParkingNearMe: PropTypes.func,
        onSpotDetailsHidden: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        onShowDetailsClick: PropTypes.func.isRequired,
        onChangeSort: PropTypes.func.isRequired,
    };
    static defaultProps = {
        isInfoPanelVisible: true,
    };

    _onChangeSort = ({value}) => {
        this.props.onChangeSort(value);
    };

    render() {
        const {
            sortBy,
            starts,
            ends,
            activeSearchTitle,
            isInfoPanelVisible,
            searchRequestId,
            canonicalUrl,
            city,
            spots,
            selectedSpotId,
            findParkingNearMe,
            onSpotDetailsHidden,
            searchType,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
        } = this.props;
        const numSpots = spots.length;
        const showInfoPanel =
            !selectedSpotId && city?.description && isInfoPanelVisible;
        const sortSelectItems = [
            {value: 'distance', label: 'Sort by Distance'},
            {value: 'price', label: 'Sort by Price'},
        ];
        const transientSpots = spots.filter(({selectedRate}) => {
            // ensure spots with either missing selectedRate
            // or those without the proper selectedRate.rule_type are not offered to users
            return selectedRate?.rule_type !== 'monthly';
        });

        return (
            <div className="Search">
                {searchRequestId ? (
                    <SearchLayoutDesktop
                        SearchMap={
                            <SearchMap
                                spots={transientSpots}
                                findParkingNearMe={findParkingNearMe}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                            />
                        }
                        SpotDetails={
                            <SpotDetails
                                spotId={selectedSpotId}
                                onCloseClick={onSpotDetailsHidden}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                            />
                        }
                        SpotList={
                            <SpotList
                                spots={transientSpots}
                                selectedSpotId={selectedSpotId}
                                canonicalUrl={canonicalUrl}
                                validFrom={starts}
                                validThrough={ends}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                                SpotListItem={SpotListItemTransient}
                            />
                        }
                        SpotListSorter={
                            <SpotListSorter
                                sortBy={sortBy}
                                sortSelectItems={sortSelectItems}
                                onChangeSort={this._onChangeSort}
                            />
                        }
                        SpotListSearchControls={
                            <SpotListSearchControls numSpots={numSpots} />
                        }
                        SpotListSearchControlsCondensed={
                            <SpotListSearchControlsCondensed
                                starts={starts}
                                ends={ends}
                                title={activeSearchTitle}
                            />
                        }
                        DestinationInfoPanel={
                            searchType === 'city' ? (
                                <DestinationInfoPanel
                                    title={city.display_name}
                                    imageId={city?.default_image.id}
                                    imageAlt={city.display_name}
                                    address={null}
                                    city={city.display_name}
                                    state={city.state}
                                    description={city.description}
                                />
                            ) : null
                        }
                        spots={spots}
                        selectedSpotId={selectedSpotId}
                        showInfoPanel={showInfoPanel}
                        disableKeydownEvents={isSoldOutEnabled}
                    />
                ) : (
                    <SHLoader />
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    city: {data: city},
    search: {
        data: {
            breadcrumbs: {
                activeBreadcrumb: {label: activeSearchTitle},
            },
            isInfoPanelVisible,
            searchType,
        },
    },
    searchRequest: {id: searchRequestId},
}) => {
    return {
        city,
        searchType,
        activeSearchTitle,
        isInfoPanelVisible,
        searchRequestId,
    };
};

const enhance = compose(
    WithSearch,
    WithParkingNearMe,
    connect(mapStateToProps)
);

export default enhance(SearchDesktopTransient);
